import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';

/**
 * The partner profiles registered in the backend, for the "Partner Profile"
 * select on the EDI settings page.
 *
 * Fetched rather than hardcoded: profiles live in code
 * (`partnerProfiles/index.ts`) and an id the UI offers but the backend doesn't
 * have would throw at send time. They change only on deploy, so this is cached
 * for the session.
 */
export function usePartnerProfiles() {
  return useQuery(
    orpc.edi.orderfulConfigs.listPartnerProfiles.queryOptions({
      staleTime: Infinity,
    }),
  );
}

export function useUpsertOrderfulConfig() {
  const queryClient = useQueryClient();

  return useMutation(
    orpc.edi.orderfulConfigs.upsert.mutationOptions({
      onSuccess: (_data, variables) => {
        // Invalidate the Express GET query used by useOrderfulEdiConfigValue
        void queryClient.invalidateQueries({
          queryKey: [
            `erp-integrations/orderful-edi/config-customer/${variables.customerId}/${encodeURIComponent(variables.key)}`,
          ],
        });
        void queryClient.invalidateQueries({
          queryKey: ['erp-integrations/orderful-edi/configs/search'],
        });
      },
    }),
  );
}

export function useDeleteOrderfulConfig() {
  const queryClient = useQueryClient();

  return useMutation(
    orpc.edi.orderfulConfigs.delete.mutationOptions({
      onSuccess: (_data, variables) => {
        void queryClient.invalidateQueries({
          queryKey: [
            `erp-integrations/orderful-edi/config-customer/${variables.customerId}/${encodeURIComponent(variables.key)}`,
          ],
        });
        void queryClient.invalidateQueries({
          queryKey: ['erp-integrations/orderful-edi/configs/search'],
        });
      },
    }),
  );
}

export function useImportOrderfulTransaction() {
  const queryClient = useQueryClient();

  return useMutation(
    orpc.edi.orderfulImport.importTransaction.mutationOptions({
      onSuccess: () => {
        // A successful import always creates a staged purchase order, so the
        // drafts list on /sales-orders-v2 is stale the moment this returns.
        void queryClient.invalidateQueries({
          queryKey: ['accounts-receivable/staged-purchase-orders-v2'],
        });
      },
    }),
  );
}

export function useApplyPartnerPreset() {
  const queryClient = useQueryClient();

  return useMutation(
    orpc.edi.orderfulConfigs.applyPreset.mutationOptions({
      onSuccess: (data, variables) => {
        // Invalidate every key the preset touched so the form re-fetches.
        for (const key of data.applied) {
          void queryClient.invalidateQueries({
            queryKey: [
              `erp-integrations/orderful-edi/config-customer/${variables.customerId}/${encodeURIComponent(key)}`,
            ],
          });
        }
        void queryClient.invalidateQueries({
          queryKey: ['erp-integrations/orderful-edi/configs/search'],
        });
      },
    }),
  );
}
